import type {Component} from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {NoteEditorOverrides} from 'notebook/notes/NoteEditorTypes';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import excess from 'notebook/tables/excess';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import SpreadsheetEvalMixin, {
  SpreadsheetEvalMixinProps,
  SpreadsheetEvalMixinState,
} from 'notebook/tables/spreadsheets/SpreadsheetEvalMixin';
import {
  ColumnConstraint,
  InsertableColumnInfo,
  Table,
  TableOpFunction,
  TableOpOptions,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';

export function UNSUPPORTED_OPERATION(): void {
  assertAndContinue(false, 'Unsupported operation.');
}

export const SELECTORS_LINK = {
  // Selectors are not enabled, but TableEditable still requires this
  value: [],
  selectorUsageCounts: {},
};

/**
 * Optimize table for spreadsheet evaluation.
 *
 * SpreadsheetEvalMixin has performance optimization where it checks for deep equality of the old table and
 * new table. However, in many cases the table also includes rendering information (JSX), which
 * is not guaranteed to be the same. Since this information does not have an impact on computation,
 * we simply strip it out.
 *
 * TODO(BNCH-4552): The evaluator should not take in a full table object, but only the essentials for
 *   performing evaluation (rowData, table, columnConstraints).
 */
function prepareForComputation(table: Table): Table {
  if (table._rowHeader) {
    // eslint-disable-next-line no-restricted-syntax
    table = _.omit(table, '_rowHeader');
  }
  if (table.rowLabels) {
    // eslint-disable-next-line no-restricted-syntax
    table = _.omit(table, 'rowLabels');
  }
  return table;
}

interface SimpleTableOpSelection {
  multiSelectKey: string;
  data: {
    multiActive: boolean;
    multiSelIndex: TableIndex;
    multiStartIndex: TableIndex;
    multiEndIndex: TableIndex;
    multiActualIndex: TableIndex;
  };
}

export interface SimpleSpreadsheetEvaluatingComponent<
  P extends SpreadsheetEvalMixinProps,
  S extends SpreadsheetEvalMixinState = SpreadsheetEvalMixinState,
> extends Component<P, S> {
  _isMultiEditing: () => boolean;
  _performSimpleTableOp: (
    op: TableOpFunction,
    options?: TableOpOptions
  ) => {selection: SimpleTableOpSelection | null; table: Table} | null;
  deleteSimpleTableColumns: (index: TableIndex, start: number, end: number) => void;
  deleteSimpleTableRows: (index: TableIndex, start: number, end: number) => void;
  getTable: (props: P) => Table & {columnConstraints: Array<ColumnConstraint>};
  getTableCid: () => string;
  insertSimpleTableColumnsLeft: (
    index: TableIndex,
    start: number,
    end: number,
    columnInfo?: Partial<InsertableColumnInfo>
  ) => void;
  insertSimpleTableColumnsRight: (
    index: TableIndex,
    start: number,
    end: number,
    columnInfo?: Partial<InsertableColumnInfo>
  ) => void;
  handleTableUpdate: (
    index: TableIndex,
    {table, selection}: {table: Table; selection: SimpleTableOpSelection | null}
  ) => void;
  renameSimpleTable: (index: TableIndex, newTableName: string) => void;
  reorderSimpleTableRowsOrColumns: (index: TableIndex, options: ReorderOptions) => void;
}

/**
 * Classes should implement getTable, getTableCid, handleTableUpdate, shouldDisableFormulas, spreadsheetRoot
 */
export const SimpleSpreadsheetEvalMixin = {
  mixins: [SpreadsheetEvalMixin],

  /**
   * Accessed externally via a ref.
   */
  getComputedTable<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>
  ): EvaluatedTable {
    return this.state.computedTables![this.getTable(this.props).name];
  },

  /* Required by SpreadsheetEvalMixin */
  makeFullTableMap<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>,
    props: P
  ): {[tableName: string]: Table} {
    return {
      [this.getTable(props).name]: prepareForComputation(this.getTable(props)),
    };
  },

  /* Required by SpreadsheetEvalMixin */
  selectorsFromProps<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>,
    props: P
  ): typeof SELECTORS_LINK {
    return SELECTORS_LINK;
  },

  _isMultiEditing<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>
  ): boolean {
    return this.state.editingTable === this.getTable(this.props).name;
  },

  insertSimpleTableColumnsLeft<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>,
    index: TableIndex,
    start: number,
    end: number,
    columnInfo: Partial<InsertableColumnInfo> = {}
  ): void {
    const result = this._performSimpleTableOp(
      (targetTable, documentName) =>
        excess.ops.insertColumnsLeft(targetTable, documentName, start, end, columnInfo),
      {forColumnEdit: true}
    );
    if (result == null) {
      return;
    }
    this.handleTableUpdate(index, result);
  },

  insertSimpleTableColumnsRight<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>,
    index: TableIndex,
    start: number,
    end: number,
    columnInfo: Partial<InsertableColumnInfo> = {}
  ): void {
    const result = this._performSimpleTableOp(
      (targetTable, documentName) =>
        excess.ops.insertColumnsRight(targetTable, documentName, start, end, columnInfo),
      {forColumnEdit: true}
    );
    if (result == null) {
      return;
    }
    this.handleTableUpdate(index, result);
  },

  reorderSimpleTableRowsOrColumns<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>,
    index: TableIndex,
    options: ReorderOptions
  ): void {
    const result = this._performSimpleTableOp(
      (targetTable, documentName) => excess.ops.reorderRowsOrColumns(targetTable, documentName, options),
      {forColumnEdit: true}
    );
    if (result == null) {
      return;
    }
    this.handleTableUpdate(index, result);
  },

  deleteSimpleTableColumns<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>,
    index: TableIndex,
    start: number,
    end: number
  ): void {
    const result = this._performSimpleTableOp(
      (targetTable, documentName) => excess.ops.deleteColumns(targetTable, documentName, start, end),
      {forColumnEdit: true}
    );
    if (result == null) {
      return;
    }
    this.handleTableUpdate(index, result);
  },

  deleteSimpleTableRows<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>,
    index: TableIndex,
    start: number,
    end: number
  ): void {
    const result = this._performSimpleTableOp(
      (targetTable, documentName) => excess.ops.deleteRows(targetTable, documentName, start, end),
      {forRowRemoval: true}
    );
    if (result == null) {
      return;
    }
    this.handleTableUpdate(index, result);
  },

  renameSimpleTable<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>,
    index: TableIndex,
    newTableName: string
  ): void {
    const result = this._performSimpleTableOp((targetTable, documentName, evaluatedTable, tableMap) =>
      excess.ops.renameTable(tableMap, documentName, targetTable.name, newTableName)
    );
    if (result == null) {
      return;
    }
    this.handleTableUpdate(index, result);
  },

  _performSimpleTableOp<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>,
    op: TableOpFunction,
    options?: TableOpOptions
  ): {selection: SimpleTableOpSelection | null; table: Table} | null {
    // Apply the operation to retrieve the new table map, along with the desired selection.
    const oldTable = this.getTable(this.props);
    const oldTables = {[oldTable.name]: oldTable};

    const {tables: newTables, box} = excess.ops.wrapTableOpForSingleDoc(op)(
      oldTables,
      this.state.computedTables!,
      oldTable.name
    );
    const newTable = newTables[oldTable.name];
    if (TableUtil.isInvalidOpWithWarn(oldTable, newTable, options)) {
      return null;
    }
    const selection = !box
      ? null
      : {
          multiSelectKey: TableUtil.getTableMultiSelectKey(this.getTableCid()),
          data: {
            multiActive: false,
            multiSelIndex: [box.minI, box.minJ] as TableIndex,
            multiStartIndex: [box.minI, box.minJ] as TableIndex,
            multiEndIndex: [box.maxI, box.maxJ] as TableIndex,
            multiActualIndex: [box.minI, box.minJ] as TableIndex,
          },
        };
    return {selection, table: newTable};
  },

  getTableEditableProps<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SimpleSpreadsheetEvaluatingComponent<P, S>
  ): NoteEditorOverrides {
    return {
      insertColumnsLeft: this.insertSimpleTableColumnsLeft,
      insertColumnsRight: this.insertSimpleTableColumnsRight,
      deleteColumns: this.deleteSimpleTableColumns,
      deleteRows: this.deleteSimpleTableRows,
      insertRowsAbove: UNSUPPORTED_OPERATION,
      insertRowsBelow: UNSUPPORTED_OPERATION,
      reorderRowsOrColumns: this.reorderSimpleTableRowsOrColumns,
      renameTable: this.renameSimpleTable,
      selectorsLink: SELECTORS_LINK,
      selectorsEnabled: false,
      multiEditing: this._isMultiEditing(),
    };
  },
};
